import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useControlledState } from 'ui-components/hooks/use-controlled-component-state/use-controlled-component-state';

import { createColumnDefinitionBuilder } from '../../../../../components/items-table/create-column-definition-builder';

import messages from '../messages';
import ProductTypeAheadInput from '../../../../../components/product-type-ahead-input/product-type-ahead-input';
import style from '../style.module.less';

const EditItemNameColumnId = 'edit-name';
const NamePropertyName = 'name';

const editItemNameDataBuilder = (item) => {
  return {
    id: item.id,
    name: item.name,
    readonly: item.readonly,
  };
};

const EditItemName = ({ value: item, onColumnBlur, disabled = false }) => {
  const intl = useIntl();
  const [name, setName] = useControlledState(item.name);
  const [wasTouched, setWasTouched] = useState(false);

  const onBlur = useCallback(
    (event) => {
      onColumnBlur(item.id, {
        [NamePropertyName]: event?.target?.value,
      });
      setWasTouched(true);
    },
    [onColumnBlur, item.id],
  );

  const onResultSelected = useCallback(
    (product) => {
      onColumnBlur(item.id, {
        name: product.name,
        sku: product.sku,
        productId: product.productId,
        productThumbnailUrl: product.thumbnailUrl,
        dangerousGoods: product.dangerousGoods,
        // unitPrice: product.unit, // TODO: have to fix currency input first
      });
    },
    [onColumnBlur, item.id],
  );

  const onChange = useCallback(
    (value) => {
      setName(value);
    },
    [setName],
  );

  const errorMessage = wasTouched && !name?.trim()?.length ? <FormattedMessage {...messages.itemNameValidationError} /> : undefined;

  if (item.readonly?.code) {
    return <div>{name}</div>;
  }

  return (
    <ProductTypeAheadInput
      className={style['name-input']}
      disabled={disabled}
      errorMessage={errorMessage}
      name={intl.formatMessage(messages.itemNameInputPlaceholder)}
      onBlur={onBlur}
      onChange={onChange}
      onResultSelected={onResultSelected}
      placeholder={intl.formatMessage(messages.itemNameInputPlaceholder)}
      value={name}
    />
  );
};

EditItemName.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    readonly: PropTypes.shape({
      code: PropTypes.string,
    }),
  }).isRequired,
  onColumnBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const createEditItemNameColumnDefinition = createColumnDefinitionBuilder({
  header: <FormattedMessage {...messages.itemName} />,
  accessor: EditItemNameColumnId,
  cell: EditItemName,
  width: 1.5,
});

export { EditItemNameColumnId, editItemNameDataBuilder, createEditItemNameColumnDefinition };
